package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Keychain
import com.tedd.db.Keychain_item
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Trousseaux et leurs entrées. Ce dépôt ne voit que du texte chiffré : chiffrer et
 * déchiffrer est l'affaire de [org.teddapp.project.vault.Vault], avec une clé qui ne
 * vit qu'en mémoire.
 */
class KeychainRepository(private val db: TeddDatabase) {

    fun observeKeychains(): Flow<List<Keychain>> =
        db.keychainQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeItems(keychainId: String): Flow<List<Keychain_item>> =
        db.keychainItemQueries.selectByKeychain(keychainId).asFlow().mapToList(Dispatchers.Default)

    suspend fun createKeychain(
        name: String,
        wrappedKey: String,
        kdf: String,
        icon: String = "lock",
        color: String = "#6F7D8C",
        id: String = newId("kc"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.keychainQueries.selectAll().executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        db.keychainQueries.insert(id, name.trim(), icon, color, wrappedKey, kdf, next, now, now)
        id
    }

    suspend fun renameKeychain(id: String, name: String) = withContext(Dispatchers.Default) {
        db.keychainQueries.rename(name.trim(), nowMillis(), id)
    }

    /** Nouvelle enveloppe de la clé : une phrase posée ou changée. */
    suspend fun rewrapKeychain(id: String, wrappedKey: String, kdf: String) = withContext(Dispatchers.Default) {
        db.keychainQueries.rewrap(wrappedKey, kdf, nowMillis(), id)
    }

    /** Supprime le trousseau et ses entrées. */
    suspend fun deleteKeychain(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.keychainItemQueries.softDeleteByKeychain(now, now, id)
            db.keychainQueries.softDelete(now, now, id)
        }
    }

    suspend fun createItem(keychainId: String, payload: String, id: String = newId("ki")): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.keychainItemQueries.selectByKeychain(keychainId).executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        db.keychainItemQueries.insert(id, keychainId, payload, next, now, now)
        id
    }

    suspend fun updateItem(id: String, payload: String) = withContext(Dispatchers.Default) {
        db.keychainItemQueries.updatePayload(payload, nowMillis(), id)
    }

    suspend fun deleteItem(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.keychainItemQueries.softDelete(now, now, id)
    }
}
